// eslint-disable-next-line
import React, { useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import { SimpleTreeView, TreeView } from '@mui/x-tree-view';
import { DialogContent, IconButton, DialogTitle, Typography, TextField, InputAdornment, Box, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import CustomTreeItem from '../../components/CustomTree/CustomTreeContent';
import { COLOR_GRAY } from '../../constants/color-codes';
import { ENTITY_TYPE, getDeviceType } from '../enum/ENUM';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { EntityAccessDetailEntity, RenderTree, UserAdvanceAccessDetail } from '../../types/manage-users';
import * as PXBColors from '@brightlayer-ui/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { EmptyState } from '@brightlayer-ui/react-components';
import RoleTreeItemView from '../../components/CustomTree/RoleTreeItemView';

const CustomExpandMoreIcon = () => <ExpandMoreIcon data-testid="expand-icon" />;
const CustomChevronRightIcon = () => <ChevronRightIcon data-testid="expand-icon" />;

type Props = {
    isLoggedInUser: boolean;
    fullname: string;
    firstName: string;
    userAccessDetailData: UserAdvanceAccessDetail;
    onCancelAdvance: Function;
    isAdvanceTreeDataAvailable: boolean;
};

export const ViewAccessDetailsDialog: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
    const { t } = useLanguageLocale();

    const [isDataAvailable, setDataAvailable] = useState(false);
    const [userData, setUserData] = React.useState<UserAdvanceAccessDetail>();
    const [treeData, setTreeData] = useState<RenderTree[]>([]);

    useEffect(() => {
        if ((props.userAccessDetailData.inviteByUserEntityAccessDetail && props.userAccessDetailData.inviteByUserEntityAccessDetail.length > 0) ||
            (props.userAccessDetailData.inviteUserEntityAccessDetail && props.userAccessDetailData.inviteUserEntityAccessDetail.length > 0)) {
            /*** This is to show only common orgs between logged in user and other user ***/
            if (!props.isLoggedInUser) {
                const cloneInviteUserEntityAccessDetailObj = props.userAccessDetailData.inviteUserEntityAccessDetail?.map((data) => data);
                props.userAccessDetailData.inviteUserEntityAccessDetail = [];
                props.userAccessDetailData.inviteByUserEntityAccessDetail!.forEach(element => {
                    const commonOrg = cloneInviteUserEntityAccessDetailObj?.find((data) => data.organizationId === element.organizationId);
                    if (commonOrg !== undefined) {
                        props.userAccessDetailData?.inviteUserEntityAccessDetail?.push(commonOrg);
                    }
                });
                setUserData(props.userAccessDetailData);
            }

            setDataAvailable(true);
            if (props.userAccessDetailData.inviteByUserEntityAccessDetail) {
                createTree(props.userAccessDetailData.inviteByUserEntityAccessDetail);
            }
        } else {
            setDataAvailable(false);
        }
        // eslint-disable-next-line
    }, [props.userAccessDetailData])

    const onCancelAdvanceInviteCall = () => {
        props.onCancelAdvance({});
    }

    const createTree = (data: EntityAccessDetailEntity[] | undefined): void => {
        if (data !== undefined) {
            const tree =
                data.map((itemsListOrg: EntityAccessDetailEntity, index: number) => {
                    let objOrg: RenderTree = {
                        id: "",
                        label: "",
                        children: [] as RenderTree[],
                        selected: false,
                        index: 0,
                        parentId: '',
                        entityType: '',
                        role: undefined
                    }
                    objOrg.id = itemsListOrg.organizationId;
                    objOrg.label = itemsListOrg.organizationName + '#' + itemsListOrg.buildingCount + ' ' + ((itemsListOrg.buildingCount > 1) ? t('ASSETS_DETAILS.BUILDINGS') : t('ENTITY_TYPE.BUILDING')) + '#' + ENTITY_TYPE.ORGANIZATION + '#' + index;
                    objOrg.index = index;
                    objOrg.parentId = '';
                    objOrg.entityType = ENTITY_TYPE.ORGANIZATION.toString();
                    objOrg.role = String(itemsListOrg.organizatoinRoleType);
                    objOrg.children = itemsListOrg.buildings ? itemsListOrg.buildings.map((itemsListBuilding, index) => {
                        let childObjBuilding: RenderTree = {
                            id: "",
                            label: "",
                            children: [] as RenderTree[],
                            selected: false,
                            index: 0,
                            parentId: '',
                            entityType: '',
                            disabled: false,
                            role: undefined,
                        }
                        childObjBuilding.id = itemsListBuilding.buildingId;
                        childObjBuilding.label = itemsListBuilding.buildingName + '#' + itemsListBuilding.systemCount + ' ' + ((itemsListBuilding.systemCount > 1) ? t('DEVICE_DETAILS.SYSTEMS') : t('ENTITY_TYPE.SYSTEM')) + '#' + ENTITY_TYPE.BUILDING + '#' + objOrg.id;
                        childObjBuilding.index = index;
                        childObjBuilding.parentId = objOrg.id;
                        childObjBuilding.entityType = ENTITY_TYPE.BUILDING.toString();
                        childObjBuilding.role = String(itemsListBuilding.buildingRoleType);
                        childObjBuilding.children = itemsListBuilding.systems ? itemsListBuilding.systems.map((objsystem, index) => {
                            let childObjSystem: RenderTree = {
                                id: "",
                                label: "",
                                children: [] as RenderTree[],
                                selected: false,
                                index: 0,
                                parentId: '',
                                entityType: '',
                                disabled: false,
                                role: undefined,
                            }
                            childObjSystem.id = objsystem.systemId;
                            childObjSystem.label = "\"" + objsystem.systemName + "\", " + getDeviceType(objsystem.systemType) + '#' + objsystem.luminaryCount + ' ' + ((objsystem.luminaryCount > 1) ? t('DEVICE_DETAILS.LUMINAIRES') : t('DEVICE_DETAILS.LUMINAIRE')) + '#' + ENTITY_TYPE.SYSTEM + '#' + objOrg.id + '#' + childObjBuilding.id;
                            childObjSystem.index = index;
                            childObjSystem.parentId = childObjBuilding.id;
                            childObjSystem.entityType = ENTITY_TYPE.SYSTEM.toString();
                            childObjSystem.role = String(objsystem.systemRoleType);
                            return childObjSystem;
                        }) : []
                        return childObjBuilding;
                    }) : []
                    return objOrg;
                })

            setTreeData(tree);
        }
    };

    return (
        <div data-testid="advance-page-container"
            style={{ overflow: 'scroll' }}
        >
            <DialogContent>
                <div>
                    <IconButton style={{
                        textAlign: 'right',
                        float: 'right',
                        margin: '8px 16px'
                    }}
                        data-testid="close-icon"
                        onClick={() => onCancelAdvanceInviteCall()}>
                        <Close style={{ width: 24, height: 24, }} fontSize="small" />
                    </IconButton>
                    <DialogTitle data-testid="invite-user-dialog-header-2" id="invite-user-dialog-header-2" sx={{ padding: '32px 24px 0px 24px', mb: 2, alignSelf: "stretch" }}>{props.fullname}</DialogTitle>
                    <Typography
                        id="invite-user-dialog-header-subtitle-2"
                        data-testid="invite-user-dialog-header-subtitle-2"
                        style={{ alignSelf: 'left', color: COLOR_GRAY, fontSize: 16, paddingLeft: 24, paddingBottom: 8 }} variant="body2">
                        {t('MANAGE_USERS.ADVANCED_INVITE_PLEASE_ASSIGN_ROLES', { replace: { email: props.firstName } })}
                    </Typography>
                </div>
                <div style={{ padding: 24, paddingTop: 8, paddingBottom: 8, }}>
                    <Box data-testid="organization-hierarchy-container" id="organization-hierarchy-container"
                        sx={{
                            maxHeight: '60%', flexGrow: 1, maxWidth: '100%', overflowY: 'auto',
                            padding: 2, backgroundColor: PXBColors.white[200],
                        }}
                        px={2}>
                        {!props.isAdvanceTreeDataAvailable && isDataAvailable && <Typography
                            id="no-data"
                            data-testid="no-data"
                            style={{ fontSize: 16, alignContent: 'center' }} variant="body2">
                            {t('DASHBOARD.NO_DATA')}
                        </Typography>}
                        {!isDataAvailable && <EmptyState style={{ flex: 1, height: '100%', backgroundColor: PXBColors.white[50] }}
                            icon={<CircularProgress id="progress-spinner" />} title={t('MESSAGES.LOADING')} placeholder={undefined} />}
                        {
                            props.isAdvanceTreeDataAvailable &&
                            <SimpleTreeView
                                multiSelect
                                data-testid="advance-tree-view"
                                aria-label="icon expansion"
                                selectedItems={[]}
                                onItemSelectionToggle={() => {}}
                                onSelectedItemsChange={() => {}}
                                slots={{ expandIcon: CustomChevronRightIcon, collapseIcon: CustomExpandMoreIcon }}
                                sx={{ minHeight: 200, flexGrow: 1, maxWidth: '100%', position: 'relative' }}>
                                {treeData.map((item: any, index: number) => <RoleTreeItemView key={index} data={item} index={index} setRoleTreeData={() => {}} />)}
                            </SimpleTreeView>
                        }
                    </Box>
                </div>
            </DialogContent>
        </div >
    )
}
